import { Loader, Modal, Stack } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import * as _ from "lodash-es";
import { useEffect, useState } from "react";
import { useAnlageStromstg } from "../../hooks/useAnlageStromstg";
import { useContainerRef } from "../../hooks/useContainerRef";
import { generatorsQueries } from "../../hooks/useGenerators";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../mantine/notifications/useToast";
import type { Generator } from "../../utils/backend-types";
import { COMPONENT_QUERY_KEY, CREATE_STEPS } from "../../utils/constants";
import { ObjectName } from "../../utils/enums";
import { GENERATOR_TYPES } from "../ComponentListContainer/ComponentList/ComponentEditWizard/Data/mocks/generatorMocks";
import { GeneratorBatchImportSuccessWidget } from "../ImportOrCreateStep/GeneratorBatchImportSuccessWidget/GeneratorBatchImportSuccessWidget";
import {
  GeneratorMaStRImportSuccessWidget,
  ImportOrCreateStep
} from "../ImportOrCreateStep/ImportOrCreateStep";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { AnlageStromstgFlow } from "./AnlageStromstgFlow/AnlageStromstgFlow";
import "./GeneratorWizard.scss";
import { CreateFormStep } from "./Steps/CreateFormStep";

const ADDITIONAL_CREATION_STEPS = [
  CREATE_STEPS.ANLAGE_TAX_FORM,
  CREATE_STEPS.CONFIRMATION_PAGE
];

const INITIAL_CREATION_STEPS: Array<number> = [
  CREATE_STEPS.MASTR_IMPORT_CONFIRMATION_PAGE,
  CREATE_STEPS.CREATE_FORM
];

const CREATE_TITLE = `${ObjectName.Generator} erstellen`;

interface GeneratorWizardProps {
  graphCoordinates?: { x: number; y: number };
  isOpen: boolean;
  siteId: number;
  variantId: number;
  onClose: () => void;
}

function GeneratorWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  variantId,
  onClose
}: GeneratorWizardProps) {
  const { containerNode: footerNode, containerRef: footerRef } =
    useContainerRef();
  const [title, setTitle] = useState<string>(CREATE_TITLE);

  return (
    <Modal
      className="GeneratorWizard"
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <GeneratorWizardSteps
        buttonContainer={footerNode}
        graphCoordinates={graphCoordinates}
        isOpen={isOpen}
        siteId={siteId}
        variantId={variantId}
        onClose={onClose}
        onUpdateTitle={setTitle}
      />
      <ModalFooter ref={footerRef} />
    </Modal>
  );
}

type GeneratorWizardStepsProps = Required<
  Pick<
    GeneratorWizardProps,
    "graphCoordinates" | "isOpen" | "siteId" | "variantId" | "onClose"
  >
> & {
  buttonContainer?: HTMLElement;
  onUpdateTitle: (title: string) => void;
};

/**
 * Kept separate from the modal so Mantine unmounts it on close, which resets
 * the flow back to the first step for the next open.
 */
function GeneratorWizardSteps({
  buttonContainer,
  graphCoordinates,
  isOpen,
  siteId,
  variantId,
  onClose,
  onUpdateTitle
}: GeneratorWizardStepsProps) {
  const { showToast } = useToast();
  const [step, setStep] = useState<number>(CREATE_STEPS.IMPORT_OR_CREATE);
  const [componentId, setComponentId] = useState<number | undefined>();
  const [componentName, setComponentName] = useState<string | undefined>();
  const [unhandledComponentIds, setUnhandledComponentIds] = useState<
    Array<number>
  >([]);
  const [responseData, setResponseData] = useState<Record<string, Generator>>(
    {}
  );
  const [maStRImportTaskId, setMaStRImportTaskId] = useState<
    string | undefined
  >();
  const [batchImportTaskId, setBatchImportTaskId] = useState<
    string | undefined
  >();
  const {
    data: anlageStromstgData,
    isLoading: isAnlageStromstgDataLoading,
    error: anlageStromstgDataError
  } = useAnlageStromstg(
    componentId ? responseData[componentId]?.anlageStromstg : undefined
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    onUpdateTitle(
      componentName ? `${componentName} konfigurieren` : CREATE_TITLE
    );
  }, [componentName, onUpdateTitle]);

  function handleChooseManualCreate() {
    setStep(CREATE_STEPS.CREATE_FORM);
  }

  function handleMaStRImportSuccess(taskId: string) {
    setStep(CREATE_STEPS.MASTR_IMPORT_CONFIRMATION_PAGE);
    setMaStRImportTaskId(taskId);
  }

  function handleBatchImportSuccess(taskId: string) {
    setStep(CREATE_STEPS.MASTR_IMPORT_CONFIRMATION_PAGE);
    setBatchImportTaskId(taskId);
  }

  function handleImportSuccessDone(components: Array<Generator>) {
    if (components.length > 0) {
      const newUnhandledComponentIds = components.map((c) => c.id);
      const newResponseData: Record<string, Generator> = _.keyBy(
        components,
        "id"
      );

      setUnhandledComponentIds(newUnhandledComponentIds);
      setResponseData(newResponseData);
      setNextAdditionalStep(newUnhandledComponentIds, newResponseData);
    } else {
      setStep(CREATE_STEPS.CONFIRMATION_PAGE);
    }
  }

  function handleClickCreate(responseData: Generator) {
    const newUnhandledComponentIds = [responseData.id];
    const newResponseData = { [responseData.id]: responseData };

    invalidateCachedData();
    setUnhandledComponentIds(newUnhandledComponentIds);
    setResponseData(newResponseData);
    setNextAdditionalStep(newUnhandledComponentIds, newResponseData);
  }

  function invalidateCachedData() {
    const objectCacheName = COMPONENT_QUERY_KEY[ObjectName.Generator];

    queryClient.invalidateQueries({
      queryKey: [objectCacheName, { siteOrVariantId: siteId }]
    });

    queryClient.invalidateQueries({
      queryKey: generatorsQueries.all()
    });

    queryClient.invalidateQueries({
      queryKey: ["generatorSingleEndpoint", { siteId }]
    });
  }

  function getNextAdditionalStep(currentStep: number): number {
    const steps = ADDITIONAL_CREATION_STEPS;

    if (INITIAL_CREATION_STEPS.includes(currentStep)) {
      return steps[0];
    }

    const currentStepIndex = steps.findIndex((s) => s === currentStep);

    if (currentStepIndex >= 0) {
      const nextStepIndex = (currentStepIndex + 1) % steps.length;
      return steps[nextStepIndex];
    }

    return -1;
  }

  function setNextAdditionalStep(
    newUnhandledComponentIds?: Array<number>,
    newResponseData?: Record<string, Generator>
  ) {
    const newOrStateUnhandledComponentIds =
      newUnhandledComponentIds || unhandledComponentIds;
    const newOrStateResponseData = newResponseData || responseData;
    const nextStep = getNextAdditionalStep(step);

    if (
      shouldStartHandlingNextComponent(
        nextStep,
        newOrStateUnhandledComponentIds
      )
    ) {
      setComponentId(newOrStateUnhandledComponentIds[0]);
      setComponentName(
        newOrStateResponseData[newOrStateUnhandledComponentIds[0]].name
      );
      setUnhandledComponentIds(newOrStateUnhandledComponentIds.slice(1));
    }

    if (nextStep === CREATE_STEPS.CONFIRMATION_PAGE) {
      setStep(nextStep);
      return;
    }

    setStep(nextStep);
  }

  function shouldStartHandlingNextComponent(
    step: number,
    unhandledComponentIds: Array<number>
  ) {
    const steps = ADDITIONAL_CREATION_STEPS;
    const isFirstStep = step === steps[0];

    return isFirstStep && unhandledComponentsExist(unhandledComponentIds);
  }

  function unhandledComponentsExist(unhandledComponentIds: Array<number>) {
    return unhandledComponentIds.length > 0;
  }

  useEffect(() => {
    if (!isOpen) return;
    if (step !== CREATE_STEPS.CONFIRMATION_PAGE) return;

    if (unhandledComponentIds.length > 0) {
      const nextId = unhandledComponentIds[0];
      const rest = unhandledComponentIds.slice(1);

      setComponentId(nextId);
      setComponentName(responseData[nextId]?.name);
      setUnhandledComponentIds(rest);
      setStep(ADDITIONAL_CREATION_STEPS[0]);
    } else {
      onClose();
      showToast({
        type: "success",
        message: "Der Erzeuger wurde erstellt und konfiguriert."
      });
    }
  }, [isOpen, step, unhandledComponentIds, responseData, onClose, showToast]);

  function renderStep() {
    switch (step) {
      case CREATE_STEPS.IMPORT_OR_CREATE:
        return (
          <ImportOrCreateStep
            buttonContainer={buttonContainer}
            objectName={ObjectName.Generator}
            siteId={siteId}
            variantId={variantId}
            onBatchImportSuccess={handleBatchImportSuccess}
            onCancel={onClose}
            onChooseManualCreate={handleChooseManualCreate}
            onImportSuccess={handleMaStRImportSuccess}
          />
        );
      case CREATE_STEPS.CREATE_FORM: {
        return (
          <CreateFormStep
            buttonContainer={buttonContainer}
            graphCoordinates={graphCoordinates}
            siteId={siteId}
            variantId={variantId}
            onClickCreate={handleClickCreate}
            onClose={onClose}
          />
        );
      }

      case CREATE_STEPS.ANLAGE_TAX_FORM: {
        if (!componentId) {
          return <Loader />;
        }

        const multiSite = Boolean(
          anlageStromstgData?.hasStandortuebergreifendeVerklammerung
        );

        return (
          <LoadOrError
            error={anlageStromstgDataError}
            loading={isAnlageStromstgDataLoading}
          >
            <AnlageStromstgFlow
              buttonContainer={buttonContainer}
              currentGenerator={responseData[componentId]}
              currentGeneratorIsNew
              multiSite={multiSite}
              shouldSkip={
                responseData[componentId].type !==
                GENERATOR_TYPES.EEG_ANLAGE_WIND
              }
              siteId={siteId}
              variantId={variantId}
              onSubmit={setNextAdditionalStep}
            />
          </LoadOrError>
        );
      }
      case CREATE_STEPS.CONFIRMATION_PAGE: {
        return <Loader />;
      }
      case CREATE_STEPS.MASTR_IMPORT_CONFIRMATION_PAGE: {
        if (!maStRImportTaskId && !batchImportTaskId) {
          return <Loader />;
        }

        if (maStRImportTaskId) {
          return (
            <GeneratorMaStRImportSuccessWidget
              buttonContainer={buttonContainer}
              taskId={maStRImportTaskId}
              onDoneStep={handleImportSuccessDone}
              onObjectCreated={invalidateCachedData}
            />
          );
        } else if (batchImportTaskId) {
          return (
            <GeneratorBatchImportSuccessWidget
              buttonContainer={buttonContainer}
              taskId={batchImportTaskId}
              onDoneStep={handleImportSuccessDone}
              onObjectCreated={invalidateCachedData}
            />
          );
        }

        break;
      }
      default:
        console.error("Unexpected step error!");
        return <div />;
    }
  }

  return (
    <Stack gap={0} justify="center">
      {renderStep()}
    </Stack>
  );
}

export { GeneratorWizard, GeneratorWizardProps };
